import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { timestamp } from "../../../firebase/config";
import { client } from "../../../hooks/Client";
import Location from "../../../hooks/Location";
import { QuestionnaireContext } from "../../../StateManagment/Questionnaire";
import { useTranslation } from "../../../StateManagment/TranslationContext";
import StatusBar from "./components/StatusBar";
import Message from "./components/Message";
import getQuestionnaire from "./getQuestionnaire";
import publicQuestionnaireDb from "./publicQuestionnaireDb";
import PublicQuestionnaireContext from "../../../StateManagment/PublicQuestionnaireContext";
import saveResponse from "./saveResponse";
import validateScaleAnswer from "./validateScaleAnswer";
import isQuestionAskable from "../../../helpers/questionnaire/isQuestionAskable";
import filterFieldsForMoment from "../../../helpers/questionnaire/filterFieldsForMoment";
import notDeleted from "../../../helpers/notDeleted";
import useTurnInterviewer from "./ovh/useTurnInterviewer";
import useScrollToBottom from "../../../hooks/useScrollToBottom";
import { heldFieldIndex, END_OF_QUESTIONNAIRE_INDEX } from "./interviewProgress";
import { interviewerText } from "./interviewerTexts";

/**
 * Het gesproken interview op de open vragenlijst.
 *
 * De keten draait volledig in Europa: Whisper bij OVH verstaat, Qwen bij OVH
 * antwoordt, en onze eigen stem leest voor. De spraak van de respondent wordt
 * nergens bewaard — alleen de tekst, net als bij een ingevulde vragenlijst.
 *
 * Het gesprek gaat met beurten in plaats van doorlopend. Dat betekent een korte
 * pauze nadat de respondent uitgesproken is; het ritme 'herhaal — klopt dat? —
 * pas dan opslaan' maskeert die pauze.
 *
 * AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md
 *
 * @param {object} props
 * @param {Array<string>} [props.excludedFieldIDs] - Vragen die op dit meetmoment niet gesteld worden
 */
const AIInterviewer = ({ excludedFieldIDs }) => {
  const { setFieldIndex, settings } = useContext(QuestionnaireContext);
  const { translations } = useTranslation();
  /** @param {string} text */
  const t = (text) => interviewerText(translations, text);

  const questionnaireId = Location()[3];
  const moment = Location()[4];
  const research = Location()[5];
  const persona = Location()[6];
  const languageCode = Location()[8];

  // Altijd binnen QuestionnaireOpen: de definitie komt uit getPublicQuestionnaire.
  const publicQuestionnaire = useContext(PublicQuestionnaireContext);
  const questionaires = publicQuestionnaire ? publicQuestionnaire.questionnaires : "";
  const fields = publicQuestionnaire ? publicQuestionnaire.fields : "";

  // Wat de respondent tot nu toe antwoordde; nodig om een toon-als-regel te
  // kunnen beoordelen, want de Responses-context wordt op dit pad niet gevuld.
  const answersRef = useRef([]);
  const [ended, setEnded] = useState(false);
  /** @type {React.RefObject<HTMLDivElement>} */
  const eventsContainerRef = useRef(null);

  const askableFields = useMemo(
    () => filterFieldsForMoment((fields || []).filter(notDeleted), excludedFieldIDs),
    [fields, excludedFieldIDs]
  );
  const questionCount = askableFields.filter((f) => f.Type !== "section").length;
  const ready = Boolean(questionaires?.length && fields);

  const buildRequest = useCallback(async () => ({
    questionnaireId,
    research,
    moment,
    persona,
    sessionId: `${questionnaireId}-${moment}-${persona}`,
    questionsList: await getQuestionnaire(publicQuestionnaireDb(publicQuestionnaire), questionaires, excludedFieldIDs),
  }), [questionnaireId, research, moment, persona, questionaires, excludedFieldIDs, publicQuestionnaire]);

  const interviewer = useTurnInterviewer({
    languageCode,
    buildRequest,
    onToolCall: async ({ callId, name, args }) => {
      if (name === "endInterview") {
        setEnded(true);
        interviewer.stop();
        setFieldIndex(END_OF_QUESTIONNAIRE_INDEX);
        // `null`: niet doorvragen, het gesprek is klaar.
        return null;
      }

      if (name === "saveResponse" && args?.input) {
        // Harde grens: het model krijgt de voorwaarde als `AskOnlyIf` mee, maar
        // een model dat die negeert mag geen antwoord opleveren op een vraag die
        // deze respondent helemaal niet gesteld had mogen krijgen.
        const askable = isQuestionAskable({
          fields: askableFields,
          questionId: args.questionId,
          answers: answersRef.current,
        });
        if (!askable) {
          return { saved: false, reason: "Deze vraag hoort bij deze respondent niet gesteld te worden. Sla hem over en ga verder." };
        }

        // Tweede harde grens: een schaalantwoord buiten de schaal ('tien' op
        // 1 tot 5) komt niet in de resultaten; Decca vraagt opnieuw.
        const scaleCheck = validateScaleAnswer(
          askableFields.find((f) => f.ID === args.questionId),
          args.input
        );
        if (!scaleCheck.ok) return { saved: false, reason: scaleCheck.reason };

        answersRef.current = [
          ...answersRef.current,
          {
            FieldID: args.questionId,
            Input: args.input,
            Itemid: args.itemId || "",
            RowID: args.rowId || "",
            ColumnID: args.columnId || "",
            FieldType: args.questionType || "",
          },
        ];

        let uitkomst = { saved: true };
        await saveResponse(
          args,
          callId,
          questionnaireId,
          client,
          research,
          moment,
          persona,
          timestamp,
          () => {},
          (index) => setFieldIndex(heldFieldIndex(index, questionCount)),
          interviewer.eventsRef,
          (_id, result) => { uitkomst = result; },
          fields,
          settings?.addressStep,
          settings?.nameStep
        );
        return uitkomst;
      }

      return { saved: false, reason: "Onbekende opdracht." };
    },
  });

  // Nieuwe regels, de 'denkt na'-balk, het label 'Je kunt praten' en een
  // foutmelding komen allemaal onderaan; zonder dit moet de respondent zelf
  // naar beneden scrollen.
  useScrollToBottom(
    eventsContainerRef,
    `${interviewer.events.length}-${interviewer.busy}-${ended}-${interviewer.error}-${interviewer.voiceAvailable}`
  );

  if (!ready) return <p>{t("Laden…")}</p>;

  return (
    <div className="ai-interviewer">
      <div className="interview-container">
        <div className="events-container" ref={eventsContainerRef}>
          {!interviewer.started && (
            <>
              <Message
                speaker="Decca"
                text={t("Welkom, ik wil je een paar vragen stellen")}
              />
              <button type="button" onClick={interviewer.start}>
                {t("Klik hier om te beginnen")}
              </button>
            </>
          )}

          {interviewer.events.map((evt) => (
            <Message key={evt.id} speaker={evt.speaker === "Jij" ? t("Jij") : evt.speaker} text={evt.transcript} />
          ))}

          {interviewer.busy && (
            <StatusBar isStarting message={t("Decca denkt na…")} progress={100} />
          )}

          {interviewer.error && (
            <p className="ai-interviewer-error" role="alert">{t(interviewer.error)}</p>
          )}

          {/* Tijdens het nadenken staat de StatusBar hierboven al; één
              melding tegelijk. */}
          {interviewer.started && !ended && !interviewer.busy && (
            <div className="ai-interviewer-controls">
              <p className="ai-interviewer-status">
                {t("Je kunt praten")}
              </p>
            </div>
          )}

          {!interviewer.voiceAvailable && (
            <p className="ai-interviewer-notice">
              {t("De stem doet het even niet; je kunt de vragen meelezen.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIInterviewer;
